/**
 * P4-02: LogPanel —— 右栏消息列表。
 *
 * 取代旧 `js/ui.js:1018-1081` 的 `renderConsoleLog`：
 * - 通过 `useGameTick` 订阅 `game.console.messages`，自定义相等性避免无变化 tick 重渲染；
 * - 日期分隔：当相邻消息 `year` 或 `seasonTitle` 变化时插入 `type_date` 标题
 *   （旧实现由 `renderConsoleLog` 递归调用 `console.msg` 生成，新实现直接计算）；
 * - 渐隐：对所有 span 统一按旧公式 `opacity = 1 - (i - fadeStart) * fadeInterval`，
 *   其中 `fadeCount = logMessagesToFade + 1`、`fadeStart = maxMessages - fadeCount`、
 *   `fadeInterval = 1 / fadeCount`；
 * - 消息内容来自引擎 HTML，与旧实现同等信任级别，使用 `dangerouslySetInnerHTML`。
 */
import { useCallback, useState } from "react";
import { useGameTick } from "../../bridge/useGameTick";
import type { GamePage } from "../../bridge/GameBridge";
import LogFilters from "./LogFilters";
import RightToolbar from "./RightToolbar";

export interface LogMessage {
  id: string;
  text: string;
  type?: string;
  tag?: string;
  noBullet?: boolean;
  year?: string | null;
  seasonTitle?: string | null;
  seasonTitleShorten?: string | null;
}

interface ConsoleSnapshot {
  messages: LogMessage[];
  maxMessages: number;
  logMessagesToFade: number;
}

function getConsoleSnapshot(game: GamePage | null): ConsoleSnapshot | null {
  if (!game) {
    return null;
  }
  const console = game.console;
  if (!console) {
    return null;
  }
  return {
    // 引擎 messages 是可变数组；拷贝快照以免 tick 间突变污染 useGameTick 缓存比较。
    messages: [...(console.messages ?? [])],
    maxMessages: console.maxMessages ?? 40,
    logMessagesToFade: console.logMessagesToFade ?? 15,
  };
}

function snapEq(a: ConsoleSnapshot | null, b: ConsoleSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (
    a.maxMessages !== b.maxMessages ||
    a.logMessagesToFade !== b.logMessagesToFade ||
    a.messages.length !== b.messages.length
  ) {
    return false;
  }
  for (let i = 0; i < a.messages.length; i++) {
    const ma = a.messages[i];
    const mb = b.messages[i];
    if (
      ma.id !== mb.id ||
      ma.text !== mb.text ||
      ma.type !== mb.type ||
      ma.tag !== mb.tag ||
      ma.noBullet !== mb.noBullet ||
      ma.year !== mb.year ||
      ma.seasonTitle !== mb.seasonTitle
    ) {
      return false;
    }
  }
  return true;
}

type LogEntry =
  | { kind: "date"; id: string; text: string }
  | { kind: "msg"; id: string; message: LogMessage };

function buildEntries(messages: LogMessage[]): LogEntry[] {
  const entries: LogEntry[] = [];
  let lastYear: string | null | undefined;
  let lastSeason: string | null | undefined;

  for (const message of messages) {
    // 旧实现只在非日期消息之间比较；若数组中残留旧日期消息则跳过。
    if (message.type === "date") {
      entries.push({ kind: "date", id: message.id, text: message.text });
      continue;
    }

    const changed =
      entries.length === 0 ||
      message.year !== lastYear ||
      message.seasonTitle !== lastSeason;

    if (changed) {
      const dateText =
        message.year && message.seasonTitle
          ? `${message.year}, ${message.seasonTitle}`
          : "ui.log.link";
      // 日期 id 必须稳定且唯一，避免复用消息 id 导致 key 冲突
      entries.push({ kind: "date", id: `date-${message.id}`, text: dateText });
    }

    entries.push({ kind: "msg", id: message.id, message });
    lastYear = message.year;
    lastSeason = message.seasonTitle;
  }

  return entries;
}

function computeOpacity(
  index: number,
  maxMessages: number,
  logMessagesToFade: number,
): number | undefined {
  const fadeCount = logMessagesToFade + 1;
  const fadeStart = maxMessages - fadeCount;
  if (index <= fadeStart) {
    return undefined;
  }
  const fadeInterval = 1 / fadeCount;
  const opacity = 1 - (index - fadeStart) * fadeInterval;
  // 旧公式在最大 index 处自然收敛到 1/fadeCount，不会为负；保留原值。
  return opacity;
}

export default function LogPanel() {
  const selector = useCallback(
    (game: GamePage | null) => getConsoleSnapshot(game),
    [],
  );
  const snapshot = useGameTick(selector, snapEq);
  const [showFilters, setShowFilters] = useState(false);

  if (!snapshot) {
    return (
      <div>
        <RightToolbar showFilters={showFilters} onToggleFilters={() => setShowFilters((s) => !s)} />
        <div id="gameLog" className="game-log" />
      </div>
    );
  }

  const entries = buildEntries(snapshot.messages);

  return (
    <div>
      <RightToolbar showFilters={showFilters} onToggleFilters={() => setShowFilters((s) => !s)} />
      <LogFilters visible={showFilters} />
      <div
        id="gameLog"
        className="game-log"
        style={{ overflowY: "scroll", paddingTop: "10px", height: "100%", minHeight: "100%" }}
      >
      {entries.map((entry, index) => {
        const opacity = computeOpacity(
          index,
          snapshot.maxMessages,
          snapshot.logMessagesToFade,
        );
        const style = opacity !== undefined ? { opacity } : undefined;

        if (entry.kind === "date") {
          return (
            <span
              key={entry.id}
              className="msg type_date"
              style={style}
              dangerouslySetInnerHTML={{ __html: entry.text }}
            />
          );
        }

        const msg = entry.message;
        const classNames = ["msg"];
        if (msg.type) {
          classNames.push(`type_${msg.type}`);
        }
        if (msg.noBullet) {
          classNames.push("noBullet");
        }

        return (
          <span
            key={entry.id}
            className={classNames.join(" ")}
            style={style}
            dangerouslySetInnerHTML={{ __html: msg.text }}
          />
        );
      })}
    </div>
  </div>
);
}
